import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { ACCOUNT_DATA_ACCESS } from '@core/auth/account-data-access.token';
import { decodeJwtPayload } from '@core/auth/jwt.util';
import { API_URL } from '@core/config/api-url.token';
import { SUPPRESS_ERROR_TOAST } from '@core/http/http-context-tokens';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { EMPTY, catchError } from 'rxjs';

export const ACCOUNT_PRESENCE_HEARTBEAT_MS = 60_000;

/**
 * Best-effort proof that an authenticated Uvet client is still open. Authentication remains the
 * bearer token; no account or clinic identifier is ever supplied by the browser.
 */
@Injectable()
export class AccountPresenceHeartbeatService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly tokenService = inject(AuthTokenService);
  private readonly accountDataAccess = inject(ACCOUNT_DATA_ACCESS);
  private readonly nativeActive = signal(true);

  private interval: ReturnType<typeof setInterval> | null = null;
  private nativeStateListener: PluginListenerHandle | null = null;
  private destroyed = false;

  private readonly onOnline = (): void => this.sendNow();
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.sendNow();
  };

  constructor() {
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.listenToNativeState();

    effect(() => {
      const accessToken = this.tokenService.tokens()?.accessToken;
      const authenticated = !!accessToken;
      const active = this.nativeActive();
      const impersonating = this.isImpersonationToken(accessToken);
      this.stopInterval();
      if (!authenticated || !active || impersonating || !this.accountDataAccess()) return;

      this.sendNow();
      this.interval = setInterval(() => this.sendNow(), ACCOUNT_PRESENCE_HEARTBEAT_MS);
    });
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.stopInterval();
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    void this.nativeStateListener?.remove();
  }

  private sendNow(): void {
    if (
      !this.nativeActive()
      || !this.accountDataAccess()
      || !this.tokenService.tokens()?.accessToken
      || this.isImpersonationToken(this.tokenService.tokens()?.accessToken)
    ) return;
    this.http
      .post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.presence}`, null, {
        context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
      })
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private stopInterval(): void {
    if (this.interval === null) return;
    clearInterval(this.interval);
    this.interval = null;
  }

  // This claim only gates a best-effort UI-side effect. Authorization stays on the backend; an
  // absent/malformed token deliberately falls through to normal heartbeat behavior.
  private isImpersonationToken(accessToken: string | undefined): boolean {
    return !!accessToken && typeof decodeJwtPayload(accessToken)?.['client_impersonation_mode'] === 'string';
  }

  private async listenToNativeState(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const listener = await App.addListener('appStateChange', ({ isActive }) => {
      this.nativeActive.set(isActive);
    });
    if (this.destroyed) {
      await listener.remove();
      return;
    }
    this.nativeStateListener = listener;
  }
}
